/**
 * This file contains the functions to convert between the backend and frontend notification settings data models.
 */

import type { User } from '@bonusly/who-am-i';

import { BackendNotificationSettings, EmailPreferences, MicrosoftTeamsPreferences, NotificationConfig, NotificationSettings, SlackPreferences } from '../types';

/**
 * Convert backend format to frontend format
 *
 * FRONTEND_FORMAT (flat array):
 * [
 *   { topicId: 'posts', channel: 'email', enabled: true },
 *   { topicId: 'posts', channel: 'slack', enabled: false },
 *   { topicId: 'comments', channel: 'email', enabled: true },
 *   ...
 * ]
 *
 * BACKEND_FORMAT (nested preferences, camelCase from API):
 * {
 *   emailPreferences: {
 *     sendBonusEmails: true,
 *     sendCommentNotifications: true,
 *     ...
 *   },
 *   slackPreferences: {
 *     sendBonusNotifications: false,
 *     ...
 *   },
 * }
 */
export function backendToFrontend(backend: BackendNotificationSettings): NotificationSettings {
	// Start with all settings initialized to false
	const settings = initializeSettings();

	// Update settings based on backend values
	return settings.map((setting) => {
		const mapping = TOPIC_FIELD_MAPPINGS.find((m) => m.topicId === setting.topicId);
		if (!mapping) return setting;

		let enabled = false;

		// Check the appropriate backend field based on channel
		// Note: Slack and Teams preferences can be null if user hasn't had them created yet.
		// When null, we default to `true` to match the backend defaults (all notifications enabled).
		// This ensures what users see in the UI matches the actual backend state.
		if (setting.channel === 'email' && mapping.emailField) {
			const value = backend.emailPreferences[mapping.emailField];
			enabled = typeof value === 'boolean' ? value : false;
		} else if (setting.channel === 'slack' && mapping.slackField) {
			// Default to true when slackPreferences is null (matches backend default)
			enabled = backend.slackPreferences?.[mapping.slackField] ?? true;
		} else if (setting.channel === 'microsoftTeams' && mapping.microsoftTeamsField) {
			// Default to true when microsoftTeamsPreferences is null (matches backend default)
			enabled = backend.microsoftTeamsPreferences?.[mapping.microsoftTeamsField] ?? true;
		}

		return { ...setting, enabled };
	});
}

/**
 * Convert frontend format to backend format
 */
export function frontendToBackend(frontend: NotificationSettings): BackendNotificationSettings {
	const emailPreferences: Partial<EmailPreferences> = {};
	const slackPreferences: Partial<SlackPreferences> = {};
	const microsoftTeamsPreferences: Partial<MicrosoftTeamsPreferences> = {};

	// Map each setting back to its backend field
	frontend.forEach((setting) => {
		const mapping = TOPIC_FIELD_MAPPINGS.find((m) => m.topicId === setting.topicId);
		if (!mapping) return;

		if (setting.channel === 'email' && mapping.emailField) {
			emailPreferences[mapping.emailField] = setting.enabled;
		} else if (setting.channel === 'slack' && mapping.slackField) {
			slackPreferences[mapping.slackField] = setting.enabled;
		} else if (setting.channel === 'microsoftTeams' && mapping.microsoftTeamsField) {
			(microsoftTeamsPreferences as any)[mapping.microsoftTeamsField] = setting.enabled;
		}
	});

	return {
		emailPreferences: emailPreferences as EmailPreferences,
		slackPreferences: slackPreferences as SlackPreferences,
		microsoftTeamsPreferences: microsoftTeamsPreferences as MicrosoftTeamsPreferences,
	};
}

/**
 * Static configuration of all available notification categories, topics and channels.
 * This should reflect all possible notification settings that can be configured.
 *
 * The form uses this configuration to organize where each notification setting is displayed.
 */
export const NOTIFICATION_CONFIG: NotificationConfig = {
	categories: [
		{
			id: 'your_activity',
			topics: [
				{ id: 'posts', availableChannels: ['email', 'slack', 'microsoftTeams'] },
				{ id: 'comments', availableChannels: ['email', 'slack', 'microsoftTeams'] },
				{ id: 'allowance', availableChannels: ['email', 'slack', 'microsoftTeams'] },
				{ id: 'one_on_ones', availableChannels: ['email', 'slack', 'microsoftTeams'] },
				{ id: 'feedback', availableChannels: ['email'] },
				{ id: 'recaps', availableChannels: ['email'] },
			],
		},
		{
			id: 'others_activity',
			topics: [
				{ id: 'activity_digest', availableChannels: ['email'] },
				{ id: 'work_anniversaries', availableChannels: ['slack', 'microsoftTeams'] },
				{ id: 'birthdays', availableChannels: ['slack', 'microsoftTeams'] },
			],
		},
		{
			id: 'team_activity',
			topics: [
				{ id: 'manager_digest', availableChannels: ['email'], visibleWhen: (user: User) => user.is_manager },
				{ id: 'team_recognition_reminders', availableChannels: ['email', 'slack', 'microsoftTeams'], visibleWhen: (user: User) => user.is_manager },
				{ id: 'team_celebrations', availableChannels: ['slack', 'microsoftTeams'], visibleWhen: (user: User) => user.is_manager },
			],
		},
		{
			id: 'company_activity',
			topics: [
				{ id: 'announcements', availableChannels: ['email', 'slack', 'microsoftTeams'] },
				{ id: 'flash_points_events', availableChannels: ['email'] },
			],
		},
		{
			id: 'admin_activity',
			topics: [
				{
					id: 'admin_monthly_digest',
					availableChannels: ['email'],
					visibleWhen: (user: User) => user.global_admin || user.finance_admin || user.reports_admin,
				},
				{ id: 'pending_rewards', availableChannels: ['email'], visibleWhen: (user: User) => user.award_approver || user.global_admin },
				{ id: 'trial_resources', availableChannels: ['email'], visibleWhen: (user: User) => user.global_admin },
			],
		},
	],
};

// Mapping between topic IDs and backend field names
interface TopicFieldMapping {
	topicId: string;
	emailField?: keyof EmailPreferences;
	slackField?: keyof SlackPreferences;
	microsoftTeamsField?: keyof MicrosoftTeamsPreferences;
}

/**
 * Flat array mapping between topic IDs and backend field names (camelCase).
 * This should reflect all possible notification settings that can be configured.
 */
export const TOPIC_FIELD_MAPPINGS: TopicFieldMapping[] = [
	// Your activity
	{
		topicId: 'posts',
		emailField: 'sendBonusEmails',
		slackField: 'sendBonusNotifications',
		microsoftTeamsField: 'sendBonusNotifications',
	},
	{
		topicId: 'comments',
		emailField: 'sendCommentNotifications',
		slackField: 'sendBonusAddonNotifications',
		microsoftTeamsField: 'sendBonusAddonNotifications',
	},
	{
		topicId: 'allowance',
		emailField: 'sendReminderEmails',
		slackField: 'sendReminderNotifications',
		microsoftTeamsField: 'sendReminderNotifications',
	},
	{
		topicId: 'one_on_ones',
		emailField: 'sendOneOnOneRemindersAndUpdates',
		slackField: 'sendOneOnOneNotifications',
		microsoftTeamsField: 'sendOneOnOneNotifications',
	},
	{
		topicId: 'feedback',
		emailField: 'sendConstructiveFeedbackNotifications',
	},
	{
		topicId: 'recaps',
		emailField: 'sendReviewRecapRemindersAndUpdates',
	},
	// Others' activity
	{
		topicId: 'activity_digest',
		emailField: 'sendActivityDigest',
	},
	{
		topicId: 'work_anniversaries',
		slackField: 'receiveAnniversaryNotifications',
		microsoftTeamsField: 'receiveAnniversaryNotifications',
	},
	{
		topicId: 'birthdays',
		slackField: 'receiveBirthdayNotifications',
		microsoftTeamsField: 'receiveBirthdayNotifications',
	},
	// Your team's activity
	{
		topicId: 'manager_digest',
		emailField: 'sendManagerDigest',
	},
	{
		topicId: 'team_recognition_reminders',
		emailField: 'receiveDirectReportRecognitionReminderNotifications',
		slackField: 'receiveDirectReportRecognitionReminderNotifications',
		microsoftTeamsField: 'receiveDirectReportRecognitionReminderNotifications',
	},
	{
		topicId: 'team_celebrations',
		slackField: 'receiveDirectReportImportantCelebrationNotifications',
		microsoftTeamsField: 'receiveDirectReportImportantCelebrationNotifications',
	},
	// Company activity
	{
		topicId: 'announcements',
		emailField: 'sendAnnouncementEmails',
		slackField: 'sendAnnouncementNotifications',
		microsoftTeamsField: 'sendAnnouncementNotifications',
	},
	{
		topicId: 'flash_points_events',
		emailField: 'sendSpecialProgramEmails',
	},
	// Admin activity
	{
		topicId: 'admin_monthly_digest',
		emailField: 'sendAdminMonthlyDigest',
	},
	{
		topicId: 'pending_rewards',
		emailField: 'sendClaimNotifications',
	},
	{
		topicId: 'trial_resources',
		emailField: 'sendTrialReminders',
	},
];

/**
 * Initialize settings from config with all values set to false
 */
export function initializeSettings(): NotificationSettings {
	const settings: NotificationSettings = [];

	NOTIFICATION_CONFIG.categories.forEach((category) => {
		category.topics.forEach((topic) => {
			topic.availableChannels.forEach((channel) => {
				settings.push({
					topicId: topic.id,
					channel,
					enabled: false,
				});
			});
		});
	});

	return settings;
}
